<template>
	<div class="card mb-4">
		<div class="card-header d-flex justify-content-between align-items-center">
			<div>
				<h2 class="h5 mb-0">Technician names</h2>
				<p class="text-muted mb-0 small">
					Every technician value the district's FieldSeeker mirror carries,
					highest volume first. Assign one to a member, create a member for it,
					or ignore it.
				</p>
			</div>
			<button
				class="btn btn-outline-secondary"
				title="Refresh technician names"
				:disabled="loading"
				@click="load"
			>
				<i class="bi bi-arrow-clockwise" :class="{ spinning: loading }"></i>
			</button>
		</div>
		<div class="card-body">
			<div v-if="loading" class="text-center py-3">
				<div class="spinner-border text-primary" role="status">
					<span class="visually-hidden">Loading…</span>
				</div>
			</div>
			<template v-else>
				<div v-if="error" class="alert alert-danger" role="alert">
					{{ error }}
				</div>

				<div class="row g-2 align-items-center mb-3">
					<div class="col-sm-6">
						<div class="input-group input-group-sm">
							<span class="input-group-text"><i class="bi bi-search"></i></span>
							<input
								v-model="filter"
								type="search"
								class="form-control"
								placeholder="Filter by name"
								aria-label="Filter technician names"
							/>
						</div>
					</div>
					<div class="col-sm-6 d-flex justify-content-sm-end">
						<div class="form-check form-switch mb-0">
							<input
								id="onlyUnmapped"
								v-model="onlyUnmapped"
								class="form-check-input"
								type="checkbox"
							/>
							<label class="form-check-label" for="onlyUnmapped">
								Only undecided ({{ undecidedCount }})
							</label>
						</div>
					</div>
				</div>

				<p v-if="names.length === 0" class="text-muted mb-0">
					No technician names yet. Run a FieldSeeker sync to populate the
					district's vocabulary.
				</p>
				<p v-else-if="visible.length === 0" class="text-muted mb-0">
					No names match this filter.
				</p>
				<div v-else class="table-responsive">
					<table class="table table-sm align-middle mb-0">
						<thead>
							<tr>
								<th scope="col">Name</th>
								<th scope="col" class="text-end">Records</th>
								<th scope="col">Mapped to</th>
								<th scope="col" class="text-end">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="row in visible" :key="row.value">
								<td>
									<code>{{ row.value }}</code>
								</td>
								<td class="text-end">{{ row.records.toLocaleString() }}</td>
								<td>
									<RouterLink
										v-if="row.user"
										:to="`/_/configuration/user/${row.user.id}`"
										class="d-inline-flex align-items-center text-decoration-none"
									>
										<img
											v-if="row.user.avatar"
											:src="row.user.avatar"
											:alt="row.user.displayName"
											class="member-avatar me-2"
										/>
										<i v-else class="bi bi-person-circle me-2"></i>
										<span>
											{{ row.user.displayName }}
											<small class="text-muted d-block">
												{{ row.user.username }}
											</small>
										</span>
									</RouterLink>
									<span v-else-if="row.ignored" class="badge text-bg-secondary">
										Ignored
									</span>
									<span v-else class="badge text-bg-warning">Unmapped</span>
								</td>
								<td class="text-end text-nowrap">
									<button
										class="btn btn-sm btn-outline-primary me-1"
										:disabled="busy"
										@click="openAssign(row)"
									>
										Assign
									</button>
									<button
										class="btn btn-sm btn-outline-secondary me-1"
										:disabled="busy"
										@click="openCreate(row)"
									>
										Create user
									</button>
									<button
										class="btn btn-sm btn-outline-danger"
										:disabled="busy || row.ignored"
										@click="ignore(row)"
									>
										Ignore
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<div
					v-if="mergeOffer"
					class="alert alert-info d-flex justify-content-between align-items-center mt-3 mb-0"
					role="alert"
				>
					<span v-if="mergeOffer.previousUser && mergeOffer.user">
						<strong>{{ mergeOffer.remainingRecords.toLocaleString() }}</strong>
						record{{ mergeOffer.remainingRecords === 1 ? "" : "s" }} are still
						credited to {{ mergeOffer.previousUser.displayName }}. Merging moves
						them onto {{ mergeOffer.user.displayName }}.
					</span>
					<button
						class="btn btn-sm btn-primary"
						:disabled="busy"
						@click="mergePrevious"
					>
						Merge
					</button>
				</div>
			</template>
		</div>

		<!-- Assign an existing member -->
		<div v-if="assignRow" class="modal d-block" tabindex="-1" role="dialog">
			<div class="modal-dialog modal-dialog-centered" role="document">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Assign “{{ assignRow.value }}”</h5>
						<button
							type="button"
							class="btn-close"
							aria-label="Close"
							@click="assignRow = null"
						></button>
					</div>
					<div class="modal-body">
						<p class="text-muted small">
							Pick the member this source name belongs to. Records already
							attributed are not moved; you will be offered a merge if the
							previous owner keeps any.
						</p>
						<UserSelector
							v-model="selectedUser"
							placeholder="Search members…"
						/>
					</div>
					<div class="modal-footer">
						<button class="btn btn-outline-secondary" @click="assignRow = null">
							Cancel
						</button>
						<button
							class="btn btn-primary"
							:disabled="!selectedUser || busy"
							@click="confirmAssign"
						>
							Assign
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Create an inactive member for the value -->
		<div v-if="createRow" class="modal d-block" tabindex="-1" role="dialog">
			<div class="modal-dialog modal-dialog-centered" role="document">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">
							Create a member for “{{ createRow.value }}”
						</h5>
						<button
							type="button"
							class="btn-close"
							aria-label="Close"
							@click="createRow = null"
						></button>
					</div>
					<div class="modal-body">
						<p class="text-muted small">
							The member is created inactive: the work is attributed at once,
							and nobody can sign in until an owner sets a password and enables
							the account.
						</p>
						<div class="mb-3">
							<label class="form-label" for="createDisplayName"
								>Display name</label
							>
							<input
								id="createDisplayName"
								v-model="createDisplayName"
								type="text"
								class="form-control"
							/>
						</div>
						<div>
							<label class="form-label" for="createUsername">Username</label>
							<input
								id="createUsername"
								v-model="createUsername"
								type="text"
								class="form-control"
							/>
						</div>
					</div>
					<div class="modal-footer">
						<button class="btn btn-outline-secondary" @click="createRow = null">
							Cancel
						</button>
						<button
							class="btn btn-primary"
							:disabled="!createUsername || !createDisplayName || busy"
							@click="confirmCreate"
						>
							Create
						</button>
					</div>
				</div>
			</div>
		</div>

		<ToastNotification
			v-model:show="toastShow"
			:title="toastTitle"
			:message="toastMessage"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { RouterLink } from "vue-router";
import UserSelector from "@/components/UserSelector.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import { apiClient } from "@/client";
import type {
	TechnicianName,
	TechnicianNameAction,
	User,
	UserMergeCounts,
} from "@/type/api";

const names = ref<TechnicianName[]>([]);
const loading = ref(true);
const busy = ref(false);
const error = ref("");
const filter = ref("");
const onlyUnmapped = ref(false);

const assignRow = ref<TechnicianName | null>(null);
const selectedUser = ref<User | null>(null);
const createRow = ref<TechnicianName | null>(null);
const createDisplayName = ref("");
const createUsername = ref("");
const mergeOffer = ref<TechnicianNameAction | null>(null);

const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

// undecidedCount is what the toggle promises: names nobody has assigned or
// ignored yet, which is the work left.
const undecidedCount = computed(
	() => names.value.filter((row) => !row.user && !row.ignored).length,
);

const visible = computed(() => {
	const needle = filter.value.trim().toLowerCase();
	return names.value.filter((row) => {
		if (onlyUnmapped.value && (row.user || row.ignored)) return false;
		if (needle === "") return true;
		return row.value.toLowerCase().includes(needle);
	});
});

function toast(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

// usernameFromName mirrors the resolver's derivation (lower-case, letters and
// digits only) so the prefill matches what the import would have created.
function usernameFromName(name: string): string {
	return name
		.toLowerCase()
		.replace(/[^a-z0-9]/g, "")
		.slice(0, 30);
}

function patchRow(value: string, patch: Partial<TechnicianName>) {
	const index = names.value.findIndex((row) => row.value === value);
	if (index >= 0) {
		names.value[index] = { ...names.value[index], ...patch };
	}
}

async function load() {
	loading.value = true;
	error.value = "";
	try {
		names.value = await apiClient.JSONGet<TechnicianName[]>(
			"/api/integration/fieldseeker/technician-name",
		);
	} catch (e: any) {
		error.value = e?.message || "Failed to load the technician names.";
	} finally {
		loading.value = false;
	}
}

function openAssign(row: TechnicianName) {
	mergeOffer.value = null;
	selectedUser.value = null;
	assignRow.value = row;
}

async function confirmAssign() {
	if (!assignRow.value || !selectedUser.value) return;
	busy.value = true;
	error.value = "";
	try {
		const action = await apiClient.JSONPost<TechnicianNameAction>(
			"/api/integration/fieldseeker/technician-name/alias",
			{ value: assignRow.value.value, userId: selectedUser.value.id },
		);
		patchRow(assignRow.value.value, {
			aliasId: action.aliasId,
			ignored: false,
			user: action.user,
		});
		mergeOffer.value =
			action.previousUser && action.remainingRecords > 0 ? action : null;
		assignRow.value = null;
		toast("Assigned", "The technician name now points at that member.");
	} catch (e: any) {
		error.value = e?.message || "Failed to assign the technician name.";
	} finally {
		busy.value = false;
	}
}

function openCreate(row: TechnicianName) {
	mergeOffer.value = null;
	createRow.value = row;
	createDisplayName.value = row.value;
	createUsername.value = usernameFromName(row.value);
}

async function confirmCreate() {
	if (!createRow.value) return;
	busy.value = true;
	error.value = "";
	try {
		const action = await apiClient.JSONPost<TechnicianNameAction>(
			"/api/integration/fieldseeker/technician-name/user",
			{
				value: createRow.value.value,
				displayName: createDisplayName.value,
				username: createUsername.value,
			},
		);
		patchRow(createRow.value.value, {
			aliasId: action.aliasId,
			ignored: false,
			user: action.user,
		});
		createRow.value = null;
		toast("Member created", "It is inactive until an owner enables it.");
	} catch (e: any) {
		error.value = e?.message || "Failed to create the member.";
	} finally {
		busy.value = false;
	}
}

async function ignore(row: TechnicianName) {
	if (
		!window.confirm(
			`Ignore “${row.value}”? The import will stop offering it and will never ` +
				"create a member for it. Records already attributed keep their owner.",
		)
	) {
		return;
	}
	busy.value = true;
	error.value = "";
	try {
		const action = await apiClient.JSONPost<TechnicianNameAction>(
			"/api/integration/fieldseeker/technician-name/ignore",
			{ value: row.value },
		);
		patchRow(row.value, {
			aliasId: action.aliasId,
			ignored: true,
			user: null,
		});
		mergeOffer.value = null;
		toast("Ignored", "That name will not be attributed again.");
	} catch (e: any) {
		error.value = e?.message || "Failed to ignore the technician name.";
	} finally {
		busy.value = false;
	}
}

// mergePrevious folds the previous owner into the member just assigned, moving
// the rows the re-point could not. It is the same endpoint the member page uses,
// and it is irreversible.
async function mergePrevious() {
	if (!mergeOffer.value?.previousUser || !mergeOffer.value.user) return;
	const previous = mergeOffer.value.previousUser;
	const into = mergeOffer.value.user;
	if (
		!window.confirm(
			`Merge ${previous.displayName} into ${into.displayName}? This moves ` +
				`${mergeOffer.value.remainingRecords.toLocaleString()} record(s) and ` +
				"cannot be undone.",
		)
	) {
		return;
	}
	busy.value = true;
	error.value = "";
	try {
		const counts = await apiClient.JSONPost<UserMergeCounts>(
			`/api/user/${previous.id}/merge`,
			{ intoUserId: into.id },
		);
		mergeOffer.value = null;
		toast(
			"Merged",
			`${counts.records.toLocaleString()} record(s) and ${counts.aliases} alias(es) moved.`,
		);
		await load();
	} catch (e: any) {
		error.value = e?.message || "Failed to merge the members.";
	} finally {
		busy.value = false;
	}
}

onMounted(load);
</script>

<style scoped>
.member-avatar {
	width: 24px;
	height: 24px;
	border-radius: 50%;
	object-fit: cover;
}

.spinning {
	animation: spin 1s linear infinite;
}

@keyframes spin {
	from {
		transform: rotate(0deg);
	}
	to {
		transform: rotate(360deg);
	}
}

/* The modal markup is inline rather than bootstrap's JS plugin: the SPA drives
 * it with v-if, so the backdrop and z-index are all it needs. */
.modal.d-block {
	background: rgba(0, 0, 0, 0.4);
}
</style>
